import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { Severity, valuesExist } from "../scenarioValidator";
import type { CorrelationValidationGenerator } from "./correlationChecks";

export const correlationsAtIntakeChecks: CorrelationValidationGenerator = (
  correlationLimits,
  correlationField,
  { calcs }
) => [
  {
    name: `${correlationField}_oil_gravity_intake`,
    message: `API Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_API,
      correlationLimits.MAX_API,
      calcs.basicData.value.oilAPI
    ]),
    isInvalid:
      calcs.basicData.value.oilAPI > correlationLimits.MAX_API ||
      calcs.basicData.value.oilAPI < correlationLimits.MIN_API,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_gas_specific_gravity_intake`,
    message: `Gas Specific Gravity Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_GAS_SG,
      correlationLimits.MAX_GAS_SG,
      calcs.basicData.value.gasSpecificGravity
    ]),
    isInvalid:
      calcs.basicData.value.gasSpecificGravity > correlationLimits.MAX_GAS_SG ||
      calcs.basicData.value.gasSpecificGravity < correlationLimits.MIN_GAS_SG,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_temperature_intake`,
    message: `Temperature Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_TEMP,
      correlationLimits.MAX_TEMP,
      calcs.pumpIntakeTemperatureF.value
    ]),
    isInvalid:
      calcs.pumpIntakeTemperatureF.value > correlationLimits.MAX_TEMP ||
      calcs.pumpIntakeTemperatureF.value < correlationLimits.MIN_TEMP,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_pressure_intake`,
    message: `Pressure Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_PRESS,
      correlationLimits.MAX_PRESS,
      calcs.pumpIntakePressurePSIA.value
    ]),
    isInvalid:
      calcs.pumpIntakePressurePSIA.value > correlationLimits.MAX_PRESS ||
      calcs.pumpIntakePressurePSIA.value < correlationLimits.MIN_PRESS,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_oil_solution_gas_intake`,
    message: `Oil Solution Gas Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_RSO,
      correlationLimits.MAX_RSO,
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .oilPhaseGasSolubilitySCFSTB
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .oilPhaseGasSolubilitySCFSTB > correlationLimits.MAX_RSO ||
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .oilPhaseGasSolubilitySCFSTB < correlationLimits.MIN_RSO,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_bubblepoint_pressure_intake`,
    message: `Bubblepoint Pressure Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_PB,
      correlationLimits.MAX_PB,
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .bubblePointPressurePSIA
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .bubblePointPressurePSIA > correlationLimits.MAX_PB ||
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .bubblePointPressurePSIA < correlationLimits.MIN_PB,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_oil_formation_volume_factor_intake`,
    message: `Oil Formation Volume Factor Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_BO,
      correlationLimits.MAX_BO,
      calcs.fluidProperties.fluidPropertiesAtIntake.value.oilVolumeFactorBBLSTB
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .oilVolumeFactorBBLSTB > correlationLimits.MAX_BO ||
      calcs.fluidProperties.fluidPropertiesAtIntake.value
        .oilVolumeFactorBBLSTB < correlationLimits.MIN_BO,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_dead_oil_viscosity_intake`,
    message: `Dead Oil Viscosity Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_U_OD,
      correlationLimits.MAX_U_OD,
      calcs.fluidProperties.fluidPropertiesAtIntake.value.deadOilViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value.deadOilViscosityCP >
        correlationLimits.MAX_U_OD ||
      calcs.fluidProperties.fluidPropertiesAtIntake.value.deadOilViscosityCP <
        correlationLimits.MIN_U_OD,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_live_oil_viscosity_intake`,
    message: `Live Oil Viscosity Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_U_O,
      correlationLimits.MAX_U_O,
      calcs.fluidProperties.fluidPropertiesAtIntake.value.oilViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value.oilViscosityCP >
        correlationLimits.MAX_U_O ||
      calcs.fluidProperties.fluidPropertiesAtIntake.value.oilViscosityCP <
        correlationLimits.MIN_U_O,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_pseudo_reduced_pressure_intake`,
    message: `Pseudo-Reduced Pressure Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_PPR,
      correlationLimits.MAX_PPR,
      calcs.fluidProperties.fluidPropertiesAtIntake.value.psuedoCriticals
        .psuedoReducedPressure
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value.psuedoCriticals
        .psuedoReducedPressure > correlationLimits.MAX_PPR &&
      calcs.fluidProperties.fluidPropertiesAtIntake.value.psuedoCriticals
        .psuedoReducedPressure < correlationLimits.MIN_PPR,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: `${correlationField}_pseudo_reduced_temperature_intake`,
    message: `Pseudo-Reduced Temperature Out of Range @ Intake`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_TPR,
      correlationLimits.MAX_TPR,
      calcs.fluidProperties.fluidPropertiesAtIntake.value.psuedoCriticals
        .psuedoReducedTemperature
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtIntake.value.psuedoCriticals
        .psuedoReducedTemperature > correlationLimits.MAX_TPR &&
      calcs.fluidProperties.fluidPropertiesAtIntake.value.psuedoCriticals
        .psuedoReducedTemperature < correlationLimits.MIN_TPR,
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  }
];
